import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useCharacterOperations } from "./useCharacterOperations";
import { Loader2 } from "lucide-react";
import { characterFormSchema, CharacterFormData } from "./validation/characterSchema";
import { BasicInformationSection } from "./sections/BasicInformationSection";
import { CharacterDetailsSection } from "./sections/CharacterDetailsSection";
import { VisualAssetsSection } from "./sections/VisualAssetsSection";
import { SettingsSection } from "./sections/SettingsSection";
import { generateCharacterSlug } from "@/utils/characterSlug";

interface CharacterFormProps {
  character?: any;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CharacterForm = ({ character, isOpen, onClose, onSuccess }: CharacterFormProps) => {
  const { createCharacter, updateCharacter } = useCharacterOperations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CharacterFormData>({
    resolver: zodResolver(characterFormSchema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      personality: "",
      species: "",
      role: "",
      age_group: "",
      color_theme: "",
      image_url: "",
      avatar_url: "",
      status: "draft",
      display_order: 0,
      featured: false,
      teaching_focus: "",
      special_talents: "",
    },
  });

  useEffect(() => {
    if (character) {
      form.reset({
        name: character.name || "",
        slug: character.slug || generateCharacterSlug(character.name || ""),
        description: character.description || "",
        personality: character.personality || "",
        species: character.species || "",
        role: character.role || "",
        age_group: character.age_group || "",
        color_theme: character.color_theme || "",
        image_url: character.image_url || "",
        avatar_url: character.avatar_url || "",
        status: character.status || "draft",
        display_order: character.display_order || 0,
        featured: character.featured || false,
        teaching_focus: character.teaching_focus || "",
        special_talents: character.special_talents || "",
      });
    } else {
      form.reset({
        name: "",
        slug: "",
        description: "",
        personality: "",
        species: "",
        role: "",
        age_group: "",
        color_theme: "",
        image_url: "",
        avatar_url: "",
        status: "draft",
        display_order: 0,
        featured: false,
        teaching_focus: "",
        special_talents: "",
      });
    }
  }, [character, form]);

  const onSubmit = async (data: CharacterFormData) => {
    setIsSubmitting(true);
    try {
      if (character) {
        await updateCharacter(character.id, data);
      } else {
        await createCharacter(data);
      }
      onSuccess();
    } catch (error) {
      console.error("Failed to save character:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {character ? "Edit Character" : "Add New Character"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <BasicInformationSection control={form.control} setValue={form.setValue} />
            <CharacterDetailsSection control={form.control} />
            <VisualAssetsSection control={form.control} />
            <SettingsSection control={form.control} />

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="admin-button-primary"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {character ? "Update Character" : "Create Character"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
